import {Injectable} from "@angular/core";
import {Observable, of} from "rxjs";
import { HTTPCONSTANTS, MerchantService } from '@odin/odin-core';
import {ReceiptConfiguration} from "../models/receipt-configuration";
import {HttpClient} from "@angular/common/http";

export interface ReceiptError {
  error: string;
  id: string;
}

@Injectable({ providedIn: 'root' })
export class ReceiptSettingsService {

  constructor(private merchantService: MerchantService, private http: HttpClient) {}

  validate(config: ReceiptConfiguration, smsEnabled: boolean): { valid: boolean, errors: ReceiptError[] } {
    // validate merchant information
    if (config.enabledFor === 1 || config.enabledFor === 2) {
      // Email
      if (config.merchantDestination === 0) {
        for (const email of config.emailConfiguration.merchantEmailAddresses) {
          if (email === '') {
            return { valid: false, errors: [{ error: 'Merchant email address cannot be empty', id: 'merchantEmail' }] };
          }
          if (!email.includes('@')) {
            return { valid: false, errors: [{ error: 'One or more invalid email address', id: 'merchantEmail' }] };
          } else {
            const emailParts = email.split('@');
            if (emailParts.length > 1) {
              if (emailParts[1].indexOf('.') === -1) {
                return {valid: false, errors: [{error: 'One or more invalid email address', id: 'merchantEmail'}]};
              }
            } else {
              return { valid: false, errors: [{ error: 'One or more invalid email address', id: 'merchantEmail' }] };
            }
          }
        }
        if (!config.emailConfiguration.merchantEmailSubject?.trim()) {
          return { valid: false, errors: [{ error: 'Merchant email subject cannot be empty', id: 'merchantEmailSubject' }] };
        }
      }

      // SMS
      if (smsEnabled && config.merchantDestination === 1) {
        if (config.smsConfiguration.merchantPhoneNumber === '') {
          return { valid: false, errors: [{ error: 'Merchant phone number cannot be empty', id: 'merchantPhone' }] };
        }
        if (isNaN(Number(config.smsConfiguration.merchantPhoneNumber))) {
          return { valid: false, errors: [{ error: 'Invalid phone number', id: 'merchantPhone' }] };
        }
      }
    }

    // validate customer information
    if (config.enabledFor === 0 || config.enabledFor === 2) {
      if (config.customerDestination === 0 && !config.emailConfiguration.customerEmailSubject?.trim()) {
        return { valid: false, errors: [{ error: 'Customer email subject cannot be empty', id: 'customerEmailSubject' }] };
      }
    }

    return { valid: true, errors: [] };
  }

  getConfig(): Observable<ReceiptConfiguration> {
    const merchantRef = this.merchantService.getCurrentBuid();
    const monekId = this.merchantService.getCurrentMid()?.monekId;
    if (merchantRef === null) return of();
    return this.http.get<ReceiptConfiguration>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/transactions/receipt/${merchantRef.id}?monekId=${monekId}`,
    );
  }

  createConfig(config: ReceiptConfiguration): Observable<ReceiptConfiguration> {
    const merchantRef = this.merchantService.getCurrentBuid();
    const monekId = this.merchantService.getCurrentMid()?.monekId;
    if (merchantRef === null) return of();
    return this.http.post<ReceiptConfiguration>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/transactions/receipt/${merchantRef.id}?monekId=${monekId}`,
      config,
    );
  }

  updateConfig(config: ReceiptConfiguration): Observable<ReceiptConfiguration> {
    const merchantRef = this.merchantService.getCurrentBuid();
    const monekId = this.merchantService.getCurrentMid()?.monekId;
    if (merchantRef === null) return of();
    return this.http.put<ReceiptConfiguration>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/transactions/receipt/${merchantRef.id}?monekId=${monekId}`,
      config,
    );
  }

  deleteConfig(): Observable<object> {
    const merchantRef = this.merchantService.getCurrentBuid();
    const monekId = this.merchantService.getCurrentMid()?.monekId;
    if (merchantRef === null) return of();
    return this.http.delete(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/transactions/receipt/${merchantRef.id}?monekId=${monekId}`,
    );
  }

  resendPaymentConfirmation(crossRef: string): Observable<ReceiptConfiguration> {
    const merchantRef = this.merchantService.getCurrentBuid();
    const monekId = this.merchantService.getCurrentMid()?.monekId;
    if (merchantRef === null) return of();
    return this.http.get<ReceiptConfiguration>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}/transactions/receipt/${merchantRef.id}?monekId=${monekId}&crossRef=${crossRef}`,
    );
  }
}
